import { Badge } from "@elaniintc/goes-design-system";
import dayjs from "dayjs";
import type {
  ApplicantProfile,
  AssociationItem,
} from "./associations-list-modal.types";

export const ITEMS_PER_PAGE = 8;

export const statusBadge = (
  status: AssociationItem["authorization_status"],
  label?: string,
) => {
  const map: Record<
    string,
    { label: string; variant: "success" | "warning" | "danger" | "neutral" }
  > = {
    ACTIVE: { label: "Activa", variant: "success" },
    PENDING: { label: "Pendiente", variant: "warning" },
    REJECTED: { label: "Rechazada", variant: "danger" },
    EXPIRED: { label: "Caducada", variant: "warning" },
    UNLINKED: { label: "Desvinculada", variant: "warning" },
  };
  const s = map[status] ?? { label: status, variant: "neutral" };
  // Prefer the backend label when available (single source of truth).
  return (
    <Badge size="xs" variant={s.variant}>
      {label || s.label}
    </Badge>
  );
};

export const getPersonName = (person: ApplicantProfile) => {
  if (person.commercial_name) return person.commercial_name;
  return `${person.first_name} ${person.last_name || ""}`.trim();
};

const ROLE_LABEL: Record<string, string> = {
  institution: "Institución",
  holder: "Titular",
  representative: "Representante",
};

export const unlinkedByText = (
  unlinkedBy: NonNullable<AssociationItem["unlinked_by"]>,
) => {
  const role = ROLE_LABEL[unlinkedBy.role] ?? unlinkedBy.role;
  return unlinkedBy.name ? `${role} — ${unlinkedBy.name}` : role;
};

export const formatDate = (date?: string | null) =>
  date ? dayjs(date).format("MMM DD, YYYY | hh:mm A") : "—";

/**
 * An association is in force only when both flags agree: `active` alone stays
 * true on expired ones, and the status alone does not cover replaced ones.
 */
export const isAssociationActive = (item: AssociationItem) =>
  item.active && item.authorization_status === "ACTIVE";

/** Active associations first; inactive/unlinked keep their order below. */
export const sortActiveFirst = (items: AssociationItem[]) =>
  [...items].sort(
    (a, b) =>
      (isAssociationActive(a) ? 0 : 1) - (isAssociationActive(b) ? 0 : 1),
  );
